import React from 'react';
import { ArrowRight, Zap, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AnimatedSection from './AnimatedSection';
import { useReducedMotion } from '../hooks/useReducedMotion';
import VideoPlayer from './VideoPlayer';

export default function Hero() {
  const prefersReducedMotion = useReducedMotion();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Video Background */}
      <VideoPlayer
        className="absolute inset-0 z-0"
        style={{ opacity: 1 }}
      />

      {/* Fallback Background - removed to avoid darkening video */}
      <div className="absolute inset-0 pointer-events-none"></div>

      {/* Removed black overlay to keep video clear */}

      {/* Floating Particles */}
      {!prefersReducedMotion && (
        <div className="absolute inset-0 overflow-hidden z-15">
          <div className="particle particle-1"></div>
          <div className="particle particle-2"></div>
          <div className="particle particle-3"></div>
          <div className="particle particle-4"></div>
          <div className="particle particle-5"></div>
        </div>
      )}

      {/* Content */}
      <div className="relative z-20 text-center max-w-6xl mx-auto px-4">
        {/* Logo/Brand */}
        <AnimatedSection animationType="fade-in">
          <div className="mb-8">
            <h1 className="text-6xl sm:text-8xl md:text-9xl font-black tracking-tight mb-4">
              <span className={`bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent ${!prefersReducedMotion ? 'animate-pulse-glow' : ''}`}>
                MEDIA
              </span>
              <span className="text-white">MOGULZ</span>
            </h1>
            <div className="flex items-center justify-center gap-2 text-yellow-400 text-xl">
              <Zap className={`h-6 w-6 ${!prefersReducedMotion ? 'animate-bounce' : ''}`} />
              <span className="font-bold tracking-wider">.de</span>
              <Zap className={`h-6 w-6 ${!prefersReducedMotion ? 'animate-bounce' : ''}`} />
            </div>
          </div>
        </AnimatedSection>

        {/* Tagline */}
        <AnimatedSection animationType="fade-up" delay={200}>
          <div className="mb-8 space-y-4">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-transparent bg-gradient-to-r from-pink-500 via-yellow-400 to-cyan-400 bg-clip-text">
              Fostering Innovation. Building Empires. Disrupting Media.
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              We build, create, and collaborate with brands, news outlets, and cutting-edge software 
              to <span className="text-cyan-400 font-semibold">revolutionize media management</span>
            </p>
          </div>
        </AnimatedSection>

        {/* CTA Buttons - Deactivated */}
        <AnimatedSection animationType="fade-up" delay={400}>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mt-12">
            <Button 
              disabled
              size="lg" 
              className="bg-gradient-to-r from-gray-600 to-gray-700 text-gray-400 font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 cursor-not-allowed opacity-50 w-full sm:w-auto"
            >
              <Play className="mr-2 h-5 w-5" />
              START YOUR EMPIRE
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            
            <Button 
              disabled
              variant="outline" 
              size="lg"
              className="border-2 border-gray-600 text-gray-500 font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 cursor-not-allowed opacity-50 w-full sm:w-auto"
            >
              Explore Innovation
            </Button>
          </div>
        </AnimatedSection>

        {/* Scroll Indicator */}
        {!prefersReducedMotion && (
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce hidden md:block">
            <div className="w-6 h-10 border-2 border-cyan-400 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-cyan-400 rounded-full mt-2 animate-pulse"></div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
